<template>
  <div v-if="chipOptions.length">
    <h3 class="font-semibold leading-6 mb-3 text-init mt-0">
      Selected
    </h3>

    <BaseChipSelector
      v-model="selectedOptions"
      class="flex-wrap"
      :options="chipOptionsMapped"
      :icon-position="'right'"
      @change="onChange"
    />

    <hr class="more--options__hr">
  </div>
</template>

<script setup>
import PlusIcon from '@tagvenue/icons/24/line/general/plus.svg';
import XIcon from '@tagvenue/icons/24/line/general/x.svg';
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';

import BaseChipSelector from '@/components/Ui/Forms/BaseChipSelector.vue';
import layoutNames from '@/modules/public/search/components/SearchFilters/components/FiltersTabs/People/layoutNames.json';
import { findQuickFilterLabel } from '@/modules/public/search/services/quick-filter-label';
import searchOptions from '@/modules/public/search/store/options.json';

const emit = defineEmits(['change']);

// Get access to the Vuex store
const store = useStore();

const chipOptions = ref([]);

const addNewChip = (option) => {
  if (!option) {
    return;
  }
  const index = chipOptions.value.findIndex(a => a.value === option.value);
  if (index === -1) {
    chipOptions.value.push(option);
    return;
  }

  chipOptions.value[index].selected = true;
};

const removeChip = (option) => {
  if (!option) {
    return;
  }

  if (option.value.includes('*')) {
    // remove all chips with some filter group (e.g. layout)
    chipOptions.value = chipOptions.value.filter(a => !a.value.includes(option.value.split('*')[0]));
    return;
  }

  const index = chipOptions.value.findIndex(a => a.value === option.value);
  if (index === -1) {
    return;
  }

  chipOptions.value.splice(index, 1);
};

const unselectChip = (option) => {
  if (!option) {
    return;
  }

  if (option.value.includes('*')) {
    // uncheck all chips with some filter group (e.g. layout)
    chipOptions.value.forEach(a => {
      if (a.value.includes(option.value.split('*')[0])) {
        a.selected = false;
      }
    });
    return;
  }

  const index = chipOptions.value.findIndex(a => a.value === option.value);
  if (index === -1) {
    return;
  }

  chipOptions.value[index].selected = false;
};

const quickFilterLabelFor = (types, value) => findQuickFilterLabel(
  store.state.quickFilters,
  types,
  value,
  store.state.l10n.ISOCountryCode,
);

const styleLabelFor = (value) => searchOptions.styleOptions
  .find((option) => String(option.id) === String(value))?.label;

const setChipOptions = () => {
  const filters = store.state.filters;

  if (!filters || Object.keys(filters).length === 0) {
    return;
  }

  const roomAllFeatures = [
    ...filters.allowedEvents.options,
    ...filters.roomFeatures.options,
  ];

  unselectChip({
    value: 'layout-*',
  });
  if (filters.layout.value) {
    removeChip({
      value: 'layout-*',
    });
    addNewChip({
      value: `layout-${filters.layout.value}`,
      label: `${layoutNames[filters.layout.value]} layout`,
      selected: true,

      field: 'roomLayout',
      filterValue: filters.layout.value,
    });
  }

  if (filters.catering.value) {
    unselectChip({
      value: 'catering-*',
    });

    filters.catering.value.forEach(value => {
      value = parseInt(value, 10);
      addNewChip({
        value: `catering-${value}`,
        label: searchOptions.cateringOptions.find(option => parseInt(option.id, 10) === value)?.label,
        selected: true,

        field: 'catering',
        key: value,
        filterValue: true,
      });
    });
  }

  if (filters.roomFeatures.value) {
    unselectChip({
      value: 'roomFeatures-*',
    });
    filters.roomFeatures.value.forEach(value => {
      value = parseInt(value, 10);

      // quick fix for "filters_above_map_experiment_v3" experiment
      let label = roomAllFeatures.find(option => parseInt(option.id, 10) === value)?.label;
      if (!label && store.state.quickFilters.length) {
        label = store.state.quickFilters.find(quickFilter => (
          quickFilter.type === 'room_features' &&
          parseInt(quickFilter.values[0], 10) === value
        ))?.label;
      }

      addNewChip({
        value: `roomFeatures-${value}`,
        label,
        selected: true,

        field: 'roomFeatures',
        key: value,
        filterValue: true,

      });
    });
  }

  if (filters.venueFeatures.value) {
    unselectChip({
      value: 'venueFeatures-*',
    });

    filters.venueFeatures.value.forEach(val => {
      const value = val;

      // quick fix for "filters_above_map_experiment_v3" experiment
      let label = filters.venueFeatures.options.find(option => option.id === value)?.label;
      if (!label && store.state.quickFilters.length) {
        label = store.state.quickFilters.find(quickFilter => (
          quickFilter.type === 'venueFeatures' &&
          quickFilter.values[0] === value
        ))?.label;
      }

      addNewChip({
        value: `venueFeatures-${value}`,
        label,
        selected: true,

        field: 'venueFeatures',
        key: value,
        filterValue: true,
      });
    });
  }

  if (filters.allowedEvents.value) {
    unselectChip({
      value: 'allowedEvents-*',
    });
    filters.allowedEvents.value.forEach(value => {
      value = parseInt(value, 10);
      addNewChip({
        value: `allowedEvents-${value}`,
        label: roomAllFeatures.find(option => parseInt(option.id, 10) === value)?.label,
        selected: true,

        field: 'allowedEvents',
        key: value,
        filterValue: true,
      });
    });
  }

  if (filters.venueParts.value) {
    unselectChip({
      value: 'venueParts-*',
    });
    filters.venueParts.value.forEach(value => {
      addNewChip({
        value: `venueParts-${value}`,
        label: store.state.venuePartsList.find(option => option.value === value)?.filter_label,
        selected: true,

        field: 'venueParts',
        key: value,
        filterValue: true,
      });
    });
  }

  if (filters.venueTypes.value) {
    const venueTypes = store.state.venueTypesPerEventCategoryConfig?.[store.state.eventCategory];
    unselectChip({
      value: 'venueTypes-*',
    });
    filters.venueTypes.value.forEach(value => {
      value = parseInt(value, 10);

      // quick fix for "filters_above_map_experiment_v3" experiment
      let label = venueTypes.find(option => parseInt(option.id, 10) === value)?.label;
      if (!label && store.state.quickFilters.length) {
        label = store.state.quickFilters.find(quickFilter => (
          quickFilter.type === 'venue_type' &&
          parseInt(quickFilter.values[0], 10) === value
        ))?.label;
      }

      addNewChip({
        value: `venueTypes-${value}`,
        label,
        selected: true,

        field: 'venueTypes',
        key: value,
        filterValue: true,
      });
    });
  }

  // Labels come from the page's quick filters first, then from the Style section. A value neither
  // covers has no label to render, so it is skipped here - it stays counted on the button and
  // Clear removes it.
  if (filters.roomTags.value) {
    unselectChip({
      value: 'roomTags-*',
    });
    filters.roomTags.value.forEach(val => {
      const value = parseInt(val, 10);
      const label = quickFilterLabelFor(['room_tags'], value) ?? styleLabelFor(value);
      if (!label) {
        return;
      }

      addNewChip({
        value: `roomTags-${value}`,
        label,
        selected: true,

        field: 'roomTags',
        key: value,
        filterValue: true,
      });
    });
  }

  if (filters.customRoomFeatures.value) {
    unselectChip({
      value: 'customRoomFeatures-*',
    });
    filters.customRoomFeatures.value.forEach(value => {
      const label = quickFilterLabelFor(['room_features', 'venue_type'], value);
      if (!label) {
        return;
      }

      addNewChip({
        value: `customRoomFeatures-${value}`,
        label,
        selected: true,

        field: 'customRoomFeatures',
        key: value,
        filterValue: true,
      });
    });
  }

  unselectChip({
    value: 'customPriceRange-*',
  });
  if (filters.customPriceRange.value) {
    const value = filters.customPriceRange.value;
    const label = quickFilterLabelFor(['price_range'], value);
    if (label) {
      removeChip({
        value: 'customPriceRange-*',
      });
      addNewChip({
        value: `customPriceRange-${value}`,
        label,
        selected: true,

        field: 'customPriceRange',
        filterValue: value,
      });
    }
  }

  if (filters.accessibilityFeatures.value) { // object
    unselectChip({
      value: 'accessibilityFeatures-*',
    });
    Object.keys(filters.accessibilityFeatures.value).forEach(value => {
      if (!filters.accessibilityFeatures.value[value]) {
        return;
      }

      addNewChip({
        value: `accessibilityFeatures-${value}`,
        label: searchOptions.accessibilityFeatures.find(option => option.id === value)?.label,
        selected: true,

        field: 'accessibilityFeatures',
        key: value,
        filterValue: true,
      });
    });
  }

  if (filters.flexibleCancellation.value) {
    addNewChip({
      value: 'flexibleCancellation',
      label: 'Cancellation flexibility',
      selected: true,

      field: 'flexibleCancellation',
      filterValue: true,
    });
  } else {
    unselectChip({
      value: 'flexibleCancellation',
    });
  }

  if (filters.superVenuesOnly.value) {
    addNewChip({
      value: 'superVenuesOnly',
      label: 'Only show supervenues',
      selected: true,

      field: 'superVenuesOnly',
      filterValue: true,
    });
  } else {
    unselectChip({
      value: 'superVenuesOnly',
    });
  }

  if (filters.noAgeRestriction.value) {
    addNewChip({
      value: 'noAgeRestriction',
      label: 'No age restriction',
      selected: true,

      field: 'noAgeRestriction',
      filterValue: true,
    });
  } else {
    unselectChip({
      value: 'noAgeRestriction',
    });
  }
};

watch(() => store.state.filters, () => {
  if (!store.state.selectedFiltersReactive) {
    // clear?
    return;
  }

  setChipOptions();
}, { deep: true });

const chipOptionsMapped = computed(() => {
  return chipOptions.value.map(option => ({ ...option, iconComponent: option.selected ? XIcon : PlusIcon }));
});

const onChange = (key, value) => {
  // update selected in chipOptions
  const option = chipOptions.value.find(a => a.value === key);
  if (option) {
    option.selected = value;
    option.iconComponent = value ? XIcon : PlusIcon;
  }

  if (value) {
    emit('change', { field: option.field, value: option.filterValue, key: option.key });
  } else {
    emit('change', { field: option.field, value: null, key: option.key });
  }
};

const selectedOptions = computed(() => {
  return chipOptions.value.filter(option => option.selected).map(option => option.value);
}, { immediate: true });

onMounted(() => {
  setChipOptions();

  if (selectedOptions.value.length) {
    store.commit('setSelectedFiltersReactive', true);
  }
});
</script>
